// ========== تبدیل خطاهای سرور به پیغام فارسی ==========
//
// سرور پیغام‌های خطا را به انگلیسی برمی‌گرداند و در دو قالب مختلف:
//
//   ۱) رشته‌ی ساده  → {"detail": "Customer national code ... already exists"}
//   ۲) فهرست خطاهای اعتبارسنجی pydantic →
//      {"detail":[{"type":"missing","loc":["body","first_name"],"msg":"Field required"}]}
//
// نمایش مستقیم این متن‌ها به کاربر فارسی‌زبان بی‌فایده است و نمایش یک پیغام عمومی
// («مشکل در ثبت اطلاعات») هم علت واقعی را پنهان می‌کند. این ماژول هر دو قالب را به
// یک جمله‌ی فارسی روشن تبدیل می‌کند و اگر پیغامی را نشناخت، بر اساس کد وضعیت HTTP
// نزدیک‌ترین توضیح فارسی را برمی‌گرداند.

/** برچسب فارسی فیلدهای سرور؛ برای ساختن پیغام‌های اعتبارسنجی. */
const FIELD_LABELS: Record<string, string> = {
  name: "نام",
  title: "عنوان",
  code: "کد",
  description: "توضیحات",
  first_name: "نام",
  last_name: "نام خانوادگی",
  customer_type: "نوع مشتری",
  national_code: "کد ملی",
  national_id: "شناسه ملی",
  economic_code: "کد اقتصادی",
  phone_number: "شماره همراه",
  postal_code: "کد پستی",
  address: "آدرس",
  province_id: "استان",
  city_id: "شهر",
  measurement_unit_id: "واحد اندازه‌گیری",
  product_type_id: "نوع کالا",
  goods_service_id: "کالا/خدمت",
  warehouse_id: "انبار",
  tax_rate: "نرخ مالیات",
  price: "قیمت",
  unit_price: "قیمت واحد",
  amount: "مبلغ",
  quantity: "تعداد",
  length: "طول",
  width: "عرض",
  thickness: "ضخامت",
  density: "دانسیته",
  weight: "وزن",
  username: "نام کاربری",
  password: "رمز عبور",
  email: "ایمیل",
  role_id: "نقش",
  is_active: "وضعیت فعال بودن",
  customer_id: "مشتری",
  currency_id: "ارز",
  payment_type_id: "نوع پرداخت",
  sale_type: "نوع فروش",
  invoice_date: "تاریخ فاکتور",
  valid_until: "اعتبار تا تاریخ",
  due_date: "سررسید",
  deposit_date: "تاریخ واریز",
  tracking_code: "کد رهگیری",
  receipt_file: "فایل رسید",
  accounting_confirmed: "تأیید حسابداری",
  minimum_payment_percentage: "حداقل درصد پرداخت",
  minimum_with_sales_manager_approval: "حداقل با تأیید مدیر فروش",
  maximum_with_sales_manager_approval: "حداکثر با تأیید مدیر فروش",
  item_status: "وضعیت قلم",
  order_status: "وضعیت سفارش",
  items: "اقلام",

  // ---- شناسه‌های مسیر (loc = ["path", ...]) ----
  invoice_id: "شناسه فاکتور",
  item_id: "شناسه قلم",
  payment_id: "شناسه پرداخت",
  price_id: "شناسه قیمت",
  product_id: "محصول",
  user_id: "شناسه کاربر",

  // ---- مشخصات پانل ----
  panel_type: "نوع پانل",
  panel_thickness: "ضخامت پانل",
  has_notch_top: "نک‌دار رو",
  has_notch_bottom: "نک‌دار زیر",
  has_notch: "نک‌دار",
  top_sheet_material: "ورق رو",
  top_sheet_thickness: "ضخامت ورق رو",
  top_sheet_color: "رنگ ورق رو",
  bottom_sheet_material: "ورق زیر",
  bottom_sheet_thickness: "ضخامت ورق زیر",
  bottom_sheet_color: "رنگ ورق زیر",
  foam_type: "نوع فوم",
  foam_density: "دانسیته فوم",
  specification: "مشخصات",

  // ---- کاربر و ورود ----
  fullname: "نام و نام خانوادگی",
  sales_group: "گروه فروش",
  disabled: "غیرفعال",
  grant_type: "نوع اعتبارسنجی",
  client_id: "شناسه کلاینت",
  client_secret: "کلید کلاینت",
  scope: "دامنه دسترسی",

  // ---- صفحه‌بندی و جست‌وجو ----
  page: "شماره صفحه",
  size: "تعداد در صفحه",
  search: "عبارت جست‌وجو",
};

/**
 * وقتی فیلدی در جدول بالا نبود، دست‌کم نام انگلیسی خام به کاربر نشان داده نشود.
 * برچسب‌های عمومی برای پسوندهای رایج ساخته می‌شود و در نهایت «این مقدار».
 */
const fallbackLabel = (field: string): string => {
  if (/_id$/.test(field)) return "شناسه انتخاب‌شده";
  if (/_at$|_date$/.test(field)) return "تاریخ";
  if (/_code$/.test(field)) return "کد";
  if (/_name$|^name$/.test(field)) return "نام";
  if (/amount|price|cost/.test(field)) return "مبلغ";
  return "";
};

/** نام فیلد را از loc خطای pydantic بیرون می‌کشد: ["body","first_name"] → «نام». */
const labelFromLoc = (loc: unknown): string => {
  if (!Array.isArray(loc)) return "";
  // اولین بخش معمولاً body/query/path است و نام واقعی فیلد بعد از آن می‌آید.
  const parts = loc.filter(
    (part) => typeof part === "string" && !["body", "query", "path", "header"].includes(part)
  ) as string[];
  const field = parts[parts.length - 1];
  if (!field) return "";
  // نام خام انگلیسی هیچ‌وقت به کاربر نشان داده نمی‌شود.
  return FIELD_LABELS[field] || fallbackLabel(String(field));
};

/**
 * پیغام‌های رشته‌ای شناخته‌شده‌ی سرور.
 * کلید، بخشی از متن انگلیسی است (با حروف کوچک) و مقایسه به‌صورت «شامل بودن» انجام
 * می‌شود تا تغییرات جزئی متن سرور پیغام فارسی را از کار نیندازد.
 */
const MESSAGE_RULES: Array<{ match: RegExp; text: string }> = [
  // ---- تکراری بودن ----
  {
    match: /customer.*(national code|national id|phone).*already exists/i,
    text: "کد ملی، شناسه ملی یا شماره همراه واردشده قبلاً برای مشتری دیگری ثبت شده است. لطفاً مقادیر را بررسی کنید.",
  },
  {
    match: /username.*already exists/i,
    text: "این نام کاربری قبلاً ثبت شده است. نام کاربری دیگری انتخاب کنید.",
  },
  {
    match: /email.*already exists/i,
    text: "این ایمیل قبلاً ثبت شده است.",
  },
  {
    match: /payment type already exists/i,
    text: "این نوع پرداخت قبلاً ثبت شده است.",
  },
  {
    match: /(code|title|name).*already exists/i,
    text: "مقدار واردشده تکراری است و قبلاً ثبت شده است.",
  },
  { match: /already exists/i, text: "این رکورد قبلاً ثبت شده است." },
  { match: /duplicate/i, text: "مقدار واردشده تکراری است." },

  // ---- اعتبارسنجی کد ملی و شناسه ملی ----
  {
    match: /national[_ ]?code.*(10-digit|not valid|invalid)/i,
    text: "کد ملی واردشده معتبر نیست. کد ملی باید ۱۰ رقم و معتبر باشد.",
  },
  {
    match: /national[_ ]?id.*(11-digit|not valid|invalid)/i,
    text: "شناسه ملی واردشده معتبر نیست. شناسه ملی باید ۱۱ رقم و معتبر باشد.",
  },
  {
    match: /phone.*(not valid|invalid)/i,
    text: "شماره همراه واردشده معتبر نیست.",
  },
  {
    match: /postal[_ ]?code.*(not valid|invalid)/i,
    text: "کد پستی واردشده معتبر نیست. کد پستی باید ۱۰ رقم باشد.",
  },

  // ---- دسترسی و احراز هویت ----
  { match: /not authenticated/i, text: "نشست شما منقضی شده است. لطفاً دوباره وارد شوید." },
  {
    match: /(could not validate credentials|invalid token|token expired)/i,
    text: "اعتبار ورود شما نامعتبر یا منقضی شده است. لطفاً دوباره وارد شوید.",
  },
  {
    match: /(incorrect username or password|invalid credentials)/i,
    text: "نام کاربری یا رمز عبور اشتباه است.",
  },
  {
    match: /cannot edit proforma invoice items/i,
    text: "نقش شما اجازه‌ی ویرایش اقلام پیش‌فاکتور را ندارد.",
  },
  {
    match: /cannot view this product workflow screen/i,
    text: "نقش شما به این مرحله از گردش کار تولید دسترسی ندارد.",
  },
  {
    match: /(not enough permissions|forbidden|permission denied|do not have permission|not allowed)/i,
    text: "شما به این عملیات دسترسی ندارید.",
  },

  // ---- یافت نشدن و عملیات غیرمجاز ----
  { match: /method not allowed/i, text: "این عملیات روی سرور پشتیبانی نمی‌شود." },
  { match: /not found/i, text: "رکورد موردنظر یافت نشد. ممکن است قبلاً حذف شده باشد." },

  // ---- وابستگی و قابل‌حذف نبودن ----
  {
    match: /(in use|referenced|foreign key|cannot delete|has related)/i,
    text: "این رکورد در بخش‌های دیگر استفاده شده است و قابل حذف نیست.",
  },

  // ---- قواعد کاری پیش‌فاکتور ----
  {
    match: /(cannot|can not).*(modify|update|edit|delete).*(approved|confirmed|paid)/i,
    text: "این سفارش تأیید یا پرداخت شده است و دیگر قابل تغییر نیست.",
  },
  {
    match: /insufficient|not enough (stock|inventory|balance)/i,
    text: "موجودی کافی نیست.",
  },
];

/** پیغام پیش‌فرض بر اساس کد وضعیت HTTP، وقتی متن سرور شناخته نشد. */
const STATUS_FALLBACK: Record<number, string> = {
  400: "اطلاعات ارسال‌شده نامعتبر است. لطفاً مقادیر واردشده را بررسی کنید.",
  401: "نشست شما منقضی شده است. لطفاً دوباره وارد شوید.",
  403: "شما به این عملیات دسترسی ندارید.",
  404: "رکورد موردنظر یافت نشد. ممکن است قبلاً حذف شده باشد.",
  405: "این عملیات روی سرور پشتیبانی نمی‌شود.",
  409: "مقدار واردشده تکراری است یا با اطلاعات موجود تداخل دارد.",
  413: "حجم فایل ارسالی بیش از حد مجاز است.",
  415: "نوع فایل ارسالی پشتیبانی نمی‌شود.",
  422: "اطلاعات واردشده نامعتبر است. لطفاً فیلدها را بررسی کنید.",
  429: "تعداد درخواست‌ها بیش از حد مجاز است. کمی بعد دوباره تلاش کنید.",
  500: "خطای داخلی سرور رخ داد. لطفاً بعداً دوباره تلاش کنید.",
  502: "سرور در دسترس نیست. لطفاً بعداً دوباره تلاش کنید.",
  503: "سرور موقتاً در دسترس نیست. لطفاً بعداً دوباره تلاش کنید.",
  504: "پاسخی از سرور دریافت نشد. لطفاً دوباره تلاش کنید.",
};

/** آیا متن فارسی است؟ اگر بله، همان را نشان می‌دهیم و ترجمه لازم نیست. */
const isPersian = (text: string) => /[؀-ۿ]/.test(text);

/** یک پیغام رشته‌ای سرور را به فارسی برمی‌گرداند؛ اگر نشناخت، null. */
const translateMessage = (raw: string): string | null => {
  const text = String(raw).trim();
  if (!text) return null;
  if (isPersian(text)) return text;
  const rule = MESSAGE_RULES.find((r) => r.match.test(text));
  return rule ? rule.text : null;
};

/** ارقام لاتین را به فارسی برمی‌گرداند تا عدد وسط جمله‌ی فارسی ناجور نباشد. */
const fa = (value: unknown): string =>
  String(value ?? "").replace(/[0-9]/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);

/** یک آیتم از فهرست خطاهای اعتبارسنجی pydantic را به جمله‌ی فارسی تبدیل می‌کند. */
const translateValidationItem = (item: any): string => {
  const label = labelFromLoc(item?.loc);
  const withLabel = label ? `«${label}»` : "این مقدار";
  const type = String(item?.type || "");
  const ctx = item?.ctx || {};

  switch (type) {
    case "missing":
      return `${withLabel} الزامی است.`;
    case "string_too_short":
      return `${withLabel} باید حداقل ${fa(ctx.min_length)} کاراکتر باشد.`;
    case "string_too_long":
      return `${withLabel} باید حداکثر ${fa(ctx.max_length)} کاراکتر باشد.`;
    case "literal_error":
    case "enum":
      return `${withLabel} باید یکی از این مقادیر باشد: ${ctx.expected}`;
    case "int_parsing":
    case "int_type":
    case "float_parsing":
    case "float_type":
    case "decimal_parsing":
      return `${withLabel} باید عدد باشد.`;
    case "bool_parsing":
    case "bool_type":
      return `${withLabel} باید بله یا خیر باشد.`;
    case "date_parsing":
    case "date_from_datetime_parsing":
      return `${withLabel} باید یک تاریخ معتبر باشد.`;
    case "greater_than":
      return `${withLabel} باید بزرگ‌تر از ${fa(ctx.gt)} باشد.`;
    case "greater_than_equal":
      return `${withLabel} نباید کمتر از ${fa(ctx.ge)} باشد.`;
    case "less_than":
      return `${withLabel} باید کوچک‌تر از ${fa(ctx.lt)} باشد.`;
    case "less_than_equal":
      return `${withLabel} نباید بیشتر از ${fa(ctx.le)} باشد.`;
    case "string_pattern_mismatch":
      return `قالب ${withLabel} درست نیست.`;
    default: {
      // value_error و بقیه: متن خام سرور را ترجمه می‌کنیم؛ اگر نشد، جمله‌ی عمومی.
      const msg = String(item?.msg || "").replace(/^value error,\s*/i, "");
      const translated = translateMessage(msg);
      if (translated) return label ? `${withLabel}: ${translated}` : translated;
      return `مقدار ${withLabel} معتبر نیست.`;
    }
  }
};

/**
 * خطای axios را به یک پیغام فارسی قابل‌نمایش تبدیل می‌کند.
 *
 * @param error    خطای گرفته‌شده در catch
 * @param fallback پیغام فارسی مخصوص همان صفحه، وقتی سرور توضیح قابل‌فهمی نداد
 */
export const apiErrorMessage = (error: any, fallback: string): string => {
  // قطع ارتباط یا تایم‌اوت: اصلاً پاسخی از سرور نیامده است.
  if (error && !error.response) {
    if (error.code === "ECONNABORTED") {
      return "زمان پاسخ سرور به پایان رسید. لطفاً دوباره تلاش کنید.";
    }
    if (error.message === "Network Error") {
      return "ارتباط با سرور برقرار نشد. اتصال اینترنت خود را بررسی کنید.";
    }
  }

  const status: number | undefined = error?.response?.status;
  const detail = error?.response?.data?.detail;

  // قالب ۲: فهرست خطاهای اعتبارسنجی
  if (Array.isArray(detail) && detail.length > 0) {
    const lines = detail.map(translateValidationItem).filter(Boolean);
    if (lines.length > 0) return Array.from(new Set(lines)).join("\n");
  }

  // قالب ۱: رشته‌ی ساده
  if (typeof detail === "string") {
    const translated = translateMessage(detail);
    if (translated) return translated;
  }

  // بعضی اندپوینت‌ها به‌جای detail از message استفاده می‌کنند.
  const message = error?.response?.data?.message;
  if (typeof message === "string") {
    const translated = translateMessage(message);
    if (translated) return translated;
  }

  if (status && STATUS_FALLBACK[status]) return STATUS_FALLBACK[status];
  return fallback;
};

export default apiErrorMessage;
